import React, { useCallback, useState } from 'react'
import { Stack } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { CHOICE_KIND } from './libraryEffect'
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined'
import { LibraryBadge, LibraryRow, LibraryScrollList, LibrarySearchField, writeDragData } from './LibraryListParts'
import { isTechnologyExternal } from './jobExternalCharacter'
import { libraryPageRequest, useDebouncedTerm, usePagedLibraryList } from './usePagedLibraryList'

/**
 * TechnologyLibraryList — the Technologies family (story 167, HU-04): the technologies of today
 * (`technologiesWorkflow`, `WorkflowFlow.jsx:107-111`), 20 at a time as the user scrolls (D12, D16.8). A row
 * is the technology's name; dropping it creates an empty JOB with that technology, exactly as today.
 */
export const technologyChoiceOf = (technology) => ({
  kind: CHOICE_KIND.TECHNOLOGY,
  key: 'technology:' + technology.id,
  label: technology.name, // system-value: raw-ok
  id: technology.id,
  name: technology.name, // system-value: raw-ok
  code: technology.code,
  systemDefault: technology.systemDefault
})

/**
 * PO, 29/09/2026 (the mockup's card): a technology is a capability, so the card says its code and how many
 * workstations can do it — and that it is external, when it is.
 */
const TechnologyRowComponent = ({ technology, chosen, onChooseTechnology, onDragStartTechnology, onDragEnd }) => {
  const { t_ } = useSmartTranslation()
  const workstations = Number.isInteger(technology.workstationCount)
    ? t_('{{count}} workstations', { count: technology.workstationCount })
    : ''
  return (
    <LibraryRow
      testId={'technology-row-' + technology.id}
      secondaryTestId={'technology-row-secondary-' + technology.id}
      primary={systemValueLabel(t_, 'technology', technology)}
      secondary={technology.code && technology.code !== technology.name ? technology.code : ''} // system-value: raw-ok
      icon={PrecisionManufacturingOutlinedIcon}
      facts={[{ key: 'workstations', testId: 'technology-row-workstations-' + technology.id, text: workstations }]}
      chosen={chosen}
      draggable
      onClick={() => onChooseTechnology(technology)}
      onDragStart={(event) => onDragStartTechnology(event, technology)}
      onDragEnd={onDragEnd}
    >
      {isTechnologyExternal(technology)
        ? <LibraryBadge tone='info' testId={'technology-row-external-' + technology.id}>{t_('External')}</LibraryBadge>
        : null}
    </LibraryRow>
  )
}

const TechnologyRow = React.memo(TechnologyRowComponent)

const TechnologyLibraryList = ({ active = false, fetchTechnologyPage, chosenKey = '', onChoose, onItemDragStart, onItemDragEnd }) => {
  const { t_ } = useSmartTranslation()
  const [searchText, setSearchText] = useState('')
  const term = useDebouncedTerm(searchText)
  const fetchPage = useCallback((page) => fetchTechnologyPage(libraryPageRequest({ page, term })), [fetchTechnologyPage, term])
  const list = usePagedLibraryList({ fetchPage, term, active })
  const loadNextPage = list.loadNext

  const chooseTechnology = useCallback((technology) => onChoose(technologyChoiceOf(technology)), [onChoose])
  const dragTechnology = useCallback((event, technology) => {
    writeDragData(event, { 'application/reactflow/nodeType': 'technology', 'application/reactflow/subType': technology.name }) // system-value: raw-ok
    onItemDragStart(technologyChoiceOf(technology))
  }, [onItemDragStart])

  return (
    <Stack data-testid='library-technologies' spacing={1} sx={{ flex: '1 1 0', minHeight: 0 }}>
      <LibrarySearchField testId='library-search-technologies' value={searchText} onChange={setSearchText} placeholder={t_('Search a technology…')} />
      <LibraryScrollList
        testId='library-list-technologies'
        loading={list.loading}
        failed={list.failed}
        failureText={list.failureMessage || t_('The technologies could not be loaded')}
        empty={list.items.length === 0}
        pageFailed={list.pageFailed}
        pageFailureText={list.pageFailureMessage || t_('The technologies could not be loaded')}
        onReachEnd={loadNextPage}
      >
        {list.items.map((technology) => (
          <TechnologyRow
            key={technology.id}
            technology={technology}
            chosen={chosenKey === 'technology:' + technology.id}
            onChooseTechnology={chooseTechnology}
            onDragStartTechnology={dragTechnology}
            onDragEnd={onItemDragEnd}
          />
        ))}
      </LibraryScrollList>
    </Stack>
  )
}

export default TechnologyLibraryList
